/**
 * 探索系统类型定义（统一入口）
 *
 * 本文件统一承载：
 * - 图探索运行时类型（room-graph）
 * - 探索展示卡片类型（用于房间卡牌/UI）
 * - 探索状态类型
 */

import { z } from 'zod';
import { RankSchema, type Rank } from '@/IsekaiRPG/shared/types';
import { WorldSiteTypeSchema } from '@/IsekaiRPG/modules/world/worldTypes';
import {
  ExplorationRoomDisplayType,
  type ExplorationRoomDisplayTypeValue,
  ExplorationRoomType,
} from './explorationShared';

export const ExplorationEdgeKind = z.enum(['main', 'branch', 'shortcut']);
export type ExplorationEdgeKindValue = z.infer<typeof ExplorationEdgeKind>;

export const RoomContentState = z.enum(['skeleton', 'materialized']);
export type RoomContentStateValue = z.infer<typeof RoomContentState>;

export const GraphDifficultyLevel = RankSchema;
export type GraphDifficultyLevelValue = Rank;

export const ExplorationRunStatus = z.enum(['exploring', 'completed', 'failed', 'retreated']);
export type ExplorationRunStatusValue = z.infer<typeof ExplorationRunStatus>;

// 重导出探索房间类型，供消费端从 explorationGraph 统一导入
export {
  ExplorationRoomType,
  type ExplorationRoomTypeValue,
  ExplorationRoomDisplayType,
  type ExplorationRoomDisplayTypeValue,
} from './explorationShared';

export const ExplorationRoomCardStatus = z.enum(['locked', 'available', 'visited']);
export type ExplorationRoomCardStatusValue = z.infer<typeof ExplorationRoomCardStatus>;

// ============================================
// UI 配置类型
// ============================================

export interface RoomTypeConfig {
  type: ExplorationRoomDisplayTypeValue;
  icon: string;
  label: string;
  description: string;
  color: string;
  bgColor: string;
}

export interface MechanicTagConfig {
  label: string;
  icon: string;
  color: string;
  desc: string;
}

// ============================================
// 展示房间卡片 Schema
// ============================================

export const ExplorationRoomCardSchema = z.object({
  id: z.string(),
  layer: z.number(),
  position: z.number(),
  type: ExplorationRoomDisplayType,
  title: z.string(),
  description: z.string(),
  difficulty: GraphDifficultyLevel.optional(),
  status: ExplorationRoomCardStatus,
  rewards: z.array(z.string()).optional(),
  data: z.record(z.string(), z.any()).optional(),
});
export type ExplorationRoomCard = z.infer<typeof ExplorationRoomCardSchema>;

// ============================================
// 房间与边
// ============================================

export const ExplorationRoomSchema = z.object({
  id: z.string(),
  type: ExplorationRoomType,
  gridX: z.number(),
  gridY: z.number(),
  depth: z.number(),
  tags: z.array(z.string()).default([]),
  difficulty: GraphDifficultyLevel.optional(),
  contentState: RoomContentState.default('skeleton'),
  title: z.string().optional(),
  description: z.string().optional(),
  rewards: z.array(z.string()).optional(),
  data: z.record(z.string(), z.any()).optional(),
});
export type ExplorationRoom = z.infer<typeof ExplorationRoomSchema>;

export const ExplorationEdgeSchema = z.object({
  id: z.string(),
  fromRoomId: z.string(),
  toRoomId: z.string(),
  kind: ExplorationEdgeKind,
});
export type ExplorationEdge = z.infer<typeof ExplorationEdgeSchema>;

// ============================================
// 一次探索运行数据
// ============================================

export const ExplorationGraphRunSchema = z.object({
  id: z.string(),
  siteId: z.string(),
  name: z.string(),
  siteType: WorldSiteTypeSchema,
  theme: z.string(),
  tags: z.array(z.string()).default([]),
  seed: z.string(),
  gridWidth: z.number(),
  gridHeight: z.number(),
  rooms: z.array(ExplorationRoomSchema),
  edges: z.array(ExplorationEdgeSchema),
  startRoomId: z.string(),
  bossRoomId: z.string(),
  currentRoomId: z.string(),
  visitedRoomIds: z.array(z.string()),
  reachableRoomIds: z.array(z.string()),
  status: ExplorationRunStatus.default('exploring'),
});
export type ExplorationGraphRun = z.infer<typeof ExplorationGraphRunSchema>;

// ============================================
// 探索状态
// ============================================

export const ExplorationStateSchema = z.object({
  isActive: z.boolean().default(false),
  sourceSiteId: z.string().optional(),
  graphRun: ExplorationGraphRunSchema.optional(),
});
export type ExplorationState = z.infer<typeof ExplorationStateSchema>;
